import { Velocity } from "../audio/synth";
import { MidiNote, Note, NoteRange } from "../music/note";

/**
 * Who produced the event.
 *
 * The piano is a shared bus: the player's keyboard, the MIDI input and the
 * microphone all push notes onto it, and so does the application when it
 * previews a chord or sounds a hand the player is not playing. Nothing told
 * the two apart, so a listener that validates what the player does — the
 * learning journey's `SequenceWatcher` — would count a note the app just
 * sounded as an answer, and walk the exercise forward on its own.
 */
export type EventOrigin = "player" | "app";

/**
 * Un geste sur le piano.
 *
 * `at` est l'instant du geste sur l'horloge de position, en secondes, quand
 * la source le connaît mieux que l'instant où elle en parle : un message MIDI
 * est horodaté à sa réception par le navigateur, avant que le fil principal
 * soit libre pour le traiter — une image de retard quand le roll rend, parfois
 * plus. Absent, le geste date de maintenant. Voir `gestureInstant` dans
 * `input/midi-shared.ts`, et `PianoControler`, qui en fait un tick.
 */
export type PianoEvent =
  | { type: "press"; note: Note; velocity: Velocity; origin: EventOrigin; at?: number }
  | { type: "release"; note: Note; origin: EventOrigin; at?: number }
  | { type: "sustain_on"; origin: EventOrigin; at?: number }
  | { type: "sustain_off"; origin: EventOrigin; at?: number }
  | { type: "hints_changed" }


type Listener = (event: PianoEvent) => void;

export class Piano {
  private pressed = new Map<MidiNote, Velocity>();
  /** Origin of every key currently down, so its release carries the same one. */
  private origins = new Map<MidiNote, EventOrigin>();
  /**
   * Les touches qu'une surface d'apprentissage désigne : un parcours, un
   * accord de l'atlas harmonique. Elles reçoivent le halo **et** leur nom en
   * pastille — on les montre pour qu'on les lise.
   */
  private hints = new Map<MidiNote, number>();
  /**
   * Les touches que les notes du rouleau font sonner à l'instant.
   *
   * Halo seul, sans pastille : une note qui passe n'est pas une leçon, et
   * écrire son nom à chaque fois couvrirait le clavier de texte au tempo du
   * morceau.
   *
   * Elles cèdent le pas aux précédentes. Un parcours qui montre des touches a
   * quelque chose à dire ; le rouleau, lui, ne fait que suivre la musique, et
   * il se tait tant que l'autre parle.
   */
  private sounding = new Map<MidiNote, number>();
  private listeners = new Set<Listener>();
  private sustainActive: boolean = false;

  constructor(private readonly range: NoteRange) {}

  on(listener: Listener): () => void {
    this.listeners.add(listener);

    return () => this.listeners.delete(listener);
  }

  private emit(event: PianoEvent) {
    for (const l of this.listeners) {
      l(event);
    }
  }

  press(note: Note, velocity = 0.5, origin: EventOrigin = "player", at?: number) {
    if (!this.range.contains(note)) {
      throw new Error("Out of range");
    }

    const m = note.toMidi();
    if (this.pressed.has(m)) {
      return;
    } // évite double noteOn

    this.pressed.set(m, velocity);
    this.origins.set(m, origin);
    this.emit({ type: "press", note, velocity, origin, at });
  }

  /**
   * Lifts a key, but only for whoever pressed it.
   *
   * The origin guard is not a detail: `press` is a no-op on a key that is
   * already down, so the application sounding a note the player happens to be
   * holding would otherwise lift the player's finger on its own release.
   */
  release(note: Note, origin: EventOrigin = "player", at?: number) {
    const m = note.toMidi();

    if (!this.pressed.has(m) || this.origins.get(m) !== origin) {
      return;
    } // évite double noteOff

    this.pressed.delete(m);
    this.origins.delete(m);
    this.emit({ type: "release", note, origin, at });
  }

  isPressed(note: Note): boolean {
    return this.pressed.has(note.toMidi());
  }

  getPressedVelocity(midi: MidiNote): Velocity | undefined {
    return this.pressed.get(midi);
  }

  getPressedMidis(): ReadonlyMap<MidiNote, Velocity> {
    return this.pressed;
  }

  sustainOn(origin: EventOrigin = "player", at?: number) {
    this.sustainActive = true;
    this.emit({ type: "sustain_on", origin, at });
  }
  sustainOff(origin: EventOrigin = "player", at?: number) {
    this.sustainActive = false;
    this.emit({ type: "sustain_off", origin, at });
  }

  isSustainActive(): boolean {
    return this.sustainActive;
  }

  setHints(newHints: Map<number, number>): void {
    if (sameHints(this.hints, newHints)) {
      return;
    }

    this.hints = new Map(newHints);
    this.emit({ type: "hints_changed" });
  }

  clearHints(): void {
    if (this.hints.size === 0) {
      return;
    }

    this.hints.clear();
    this.emit({ type: "hints_changed" });
  }

  /**
   * Les touches que le rouleau voit sonner. Voir {@link sounding}.
   *
   * Appelée à chaque image : elle ne prévient que sur changement, et ne
   * prévient pas du tout tant qu'une surface d'apprentissage occupe le
   * clavier — rien ne changerait à l'écran.
   */
  setSoundingHints(next: Map<number, number>): void {
    if (sameHints(this.sounding, next)) {
      return;
    }

    this.sounding = new Map(next);

    if (this.hints.size > 0) {
      return;
    }

    this.emit({ type: "hints_changed" });
  }

  /**
   * La couleur d'indication *montrée* sur cette touche, celle qui mérite son
   * nom en pastille. Voir {@link hints}.
   */
  getHint(midi: number): number | undefined {
    return this.hints.get(midi);
  }

  /**
   * La couleur du halo de cette touche : ce qu'un parcours désigne, ou à
   * défaut ce que le rouleau fait sonner.
   */
  getKeyHint(midi: number): number | undefined {
    return this.hints.get(midi) ?? this.sounding.get(midi);
  }

  allNotesOff(): void {
    // utile si blur, pointerup global, etc.
    for (const m of Array.from(this.pressed.keys())) {
      const origin = this.origins.get(m) ?? "player";
      this.pressed.delete(m);
      this.origins.delete(m);
      this.emit({ type: "release", note: Note.fromMidi(m), origin });
    }
  }

  getRange(): NoteRange {
    return this.range;
  }

  contains(note: Note): boolean {
    return this.range.contains(note);
  }

  *notes(): IterableIterator<Note> {
    yield* this.range.notes();
  }
}

export const standard88Keys = new Piano(
  new NoteRange(Note.fromName("A0"), Note.fromName("C8"))
);

/** Deux cartes d'indications identiques : de quoi ne prévenir que sur changement. */
function sameHints(a: ReadonlyMap<number, number>, b: ReadonlyMap<number, number>): boolean {
  if (a.size !== b.size) {
    return false;
  }

  for (const [midi, color] of b) {
    if (a.get(midi) !== color) {
      return false;
    }
  }

  return true;
}
